import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import {
  Plus, Pencil, Trash2, X, Loader2, Sparkles, MessageCircle, Phone,
  Home, Search, Link2, ChevronDown, ChevronUp, AlarmClock, Check, Bell,
} from "lucide-react";
import { AdminHeader } from "../../components/AdminHeader";
import { adminApi, clearToken, getToken } from "../../lib/adminApi";

const field = "w-full rounded-xl border border-navy/12 bg-white px-4 py-2.5 text-sm text-navy outline-none transition focus:border-gold focus:ring-2 focus:ring-gold/25";
const TYPES = ["", "apartment", "villa", "townhouse", "office", "plot"];
const BEDS = ["", "studio", "1", "2", "3", "4", "5"];
const DEAL_TYPES = ["", "sale", "rent"];
const dealLabel = (d) => (d === "sale" ? "For sale" : d === "rent" ? "For rent" : "—");
const UNIT_STATUS = ["available", "under_offer", "sold", "expired"];
const INQ_STATUS = ["new", "matched", "viewing", "negotiating", "won", "lost"];

const UNIT_EMPTY = { property_type: "", deal_type: "", bedrooms: "", community: "", price: "", currency: "AED", furnished: "", agent_name: "", agent_phone: "", source: "WhatsApp", notes: "", status: "available" };
const INQ_EMPTY = { buyer_name: "", buyer_phone: "", property_type: "", deal_type: "", bedrooms: "", community: "", budget_min: "", budget_max: "", currency: "AED", notes: "", status: "new" };

const fmt = (n) => (n === null || n === undefined || n === "" ? "—" : new Intl.NumberFormat("en-US").format(n));
const waLink = (p) => `https://wa.me/${(p || "").replace(/[^0-9]/g, "")}`;
const waSend = (p, text) => `https://wa.me/${(p || "").replace(/[^0-9]/g, "")}?text=${encodeURIComponent(text)}`;
const isStale = (d) => {
  if (!d) return false;
  return (Date.now() - new Date(d).getTime()) > 3 * 24 * 60 * 60 * 1000;
};
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1).replace("_", " ") : "");
const bedsLabel = (b) => (b === "studio" ? "Studio" : b ? `${b} BR` : "—");

const STATUS_STYLE = {
  available: "bg-emerald-100 text-emerald-700", under_offer: "bg-amber-100 text-amber-700",
  sold: "bg-navy/10 text-navy/60", expired: "bg-red-100 text-red-600",
  new: "bg-gold/15 text-gold", matched: "bg-blue-100 text-blue-700", viewing: "bg-indigo-100 text-indigo-700",
  negotiating: "bg-amber-100 text-amber-700", won: "bg-emerald-100 text-emerald-700", lost: "bg-red-100 text-red-600",
};

export default function AdminCrm() {
  const nav = useNavigate();
  const [tab, setTab] = useState("matches");
  const [units, setUnits] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [matchInbox, setMatchInbox] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);        // {type:'unit'|'inquiry', data}
  const [saving, setSaving] = useState(false);
  const [paste, setPaste] = useState(null);       // paste modal state
  const [matches, setMatches] = useState({});     // inquiryId -> [{unit,score,reasons}]
  const [expanded, setExpanded] = useState(null);

  const guard = (err) => { if (err.response?.status === 401) { clearToken(); nav("/admin"); } };

  const load = useCallback(async () => {
    try {
      const [u, i, m] = await Promise.all([adminApi.listUnits(), adminApi.listInquiries(), adminApi.listMatches()]);
      setUnits(u.data); setInquiries(i.data); setMatchInbox(m.data);
    } catch (err) { guard(err); toast.error("Could not load CRM data"); }
    finally { setLoading(false); }
  }, []); // eslint-disable-line

  const newMatchCount = matchInbox.filter((m) => m.status === "new").length;

  const setMatchStatus = async (id, status) => {
    try {
      await adminApi.updateMatch(id, status);
      setMatchInbox((l) => l.map((m) => m.id === id ? { ...m, status } : m));
    } catch (err) { guard(err); toast.error("Update failed"); }
  };
  const removeMatch = async (id) => {
    try { await adminApi.deleteMatch(id); setMatchInbox((l) => l.filter((m) => m.id !== id)); }
    catch (err) { guard(err); toast.error("Delete failed"); }
  };

  useEffect(() => { if (!getToken()) { nav("/admin"); return; } load(); }, [load, nav]);

  const openMatches = async (inq) => {
    if (expanded === inq.id) { setExpanded(null); return; }
    setExpanded(inq.id);
    if (!matches[inq.id]) {
      try {
        const { data } = await adminApi.inquiryMatches(inq.id);
        setMatches((m) => ({ ...m, [inq.id]: data }));
      } catch (err) { guard(err); toast.error("Could not load matches"); }
    }
  };

  const stripInput = (r, type) => {
    const base = type === "unit" ? UNIT_EMPTY : INQ_EMPTY;
    const out = {};
    Object.keys(base).forEach((k) => { out[k] = r[k] ?? base[k]; });
    // numbers
    ["price", "budget_min", "budget_max"].forEach((k) => {
      if (k in out) out[k] = out[k] === "" || out[k] === null ? null : Number(out[k]);
    });
    if ("furnished" in out) out.furnished = out.furnished === "" ? null : out.furnished === true || out.furnished === "yes" || out.furnished === "true";
    return out;
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = stripInput(form.data, form.type);
      if (form.type === "unit") {
        if (form.data.id) await adminApi.updateUnit(form.data.id, payload);
        else await adminApi.createUnit(payload);
      } else {
        if (form.data.id) await adminApi.updateInquiry(form.data.id, payload);
        else await adminApi.createInquiry(payload);
      }
      toast.success("Saved");
      setForm(null); load();
    } catch (err) { guard(err); toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const changeStatus = async (type, row, status) => {
    try {
      const payload = { ...stripInput(row, type), status };
      if (type === "unit") { await adminApi.updateUnit(row.id, payload); setUnits((l) => l.map((x) => x.id === row.id ? { ...x, status } : x)); }
      else { await adminApi.updateInquiry(row.id, payload); setInquiries((l) => l.map((x) => x.id === row.id ? { ...x, status } : x)); }
      toast.success(`Status: ${cap(status)}`);
    } catch (err) { guard(err); toast.error("Update failed"); }
  };

  const remove = async (type, id) => {
    if (!window.confirm("Delete this record?")) return;
    try {
      if (type === "unit") { await adminApi.deleteUnit(id); setUnits((l) => l.filter((x) => x.id !== id)); }
      else { await adminApi.deleteInquiry(id); setInquiries((l) => l.filter((x) => x.id !== id)); }
      toast.success("Deleted");
    } catch (err) { guard(err); toast.error("Delete failed"); }
  };

  const linkUnit = async (inq, unitId) => {
    try {
      const ids = Array.from(new Set([...(inq.linked_unit_ids || []), unitId]));
      const payload = { ...stripInput(inq, "inquiry"), linked_unit_ids: ids, status: inq.status === "new" ? "matched" : inq.status };
      await adminApi.updateInquiry(inq.id, payload);
      setInquiries((l) => l.map((x) => x.id === inq.id ? { ...x, linked_unit_ids: ids, status: payload.status } : x));
      toast.success("Unit linked to buyer");
    } catch (err) { guard(err); toast.error("Link failed"); }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, data: { ...f.data, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value } }));

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-crm">
      <AdminHeader />
      <main className="mx-auto max-w-6xl px-5 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl font-semibold text-navy">Deal CRM</h1>
            <p className="mt-1 text-sm text-muted">Match buyer inquiries with available units — nothing gets lost.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setPaste({ text: "", kind: "auto", records: null, busy: false, selected: {} })} data-testid="crm-paste-btn"
              className="inline-flex items-center gap-2 rounded-full bg-navy px-5 py-2.5 text-sm font-medium text-white transition hover:-translate-y-0.5"><Sparkles size={16} /> Paste from WhatsApp</button>
            <button onClick={() => setForm({ type: tab === "units" ? "unit" : "inquiry", data: tab === "units" ? { ...UNIT_EMPTY } : { ...INQ_EMPTY } })} data-testid="crm-new-btn"
              className="btn-primary"><Plus size={16} /> New {tab === "units" ? "unit" : "inquiry"}</button>
          </div>
        </div>

        {/* tabs */}
        <div className="mt-8 flex gap-1 rounded-full bg-white p-1 shadow-soft" data-testid="crm-tabs" style={{ width: "fit-content" }}>
          <button onClick={() => setTab("matches")} data-testid="crm-tab-matches" className={`relative rounded-full px-5 py-2 text-sm font-medium transition ${tab === "matches" ? "bg-navy text-white" : "text-navy/70 hover:bg-navy/5"}`}><Bell size={14} className="mr-1.5 inline" /> Matches{!loading && newMatchCount > 0 && <span className="ml-1.5 rounded-full bg-gold px-1.5 py-0.5 text-[10px] font-bold text-white" data-testid="crm-match-badge">{newMatchCount}</span>}</button>
          <button onClick={() => setTab("inquiries")} data-testid="crm-tab-inquiries" className={`rounded-full px-5 py-2 text-sm font-medium transition ${tab === "inquiries" ? "bg-navy text-white" : "text-navy/70 hover:bg-navy/5"}`}><Search size={14} className="mr-1.5 inline" /> Inquiries{!loading ? ` (${inquiries.length})` : ""}</button>
          <button onClick={() => setTab("units")} data-testid="crm-tab-units" className={`rounded-full px-5 py-2 text-sm font-medium transition ${tab === "units" ? "bg-navy text-white" : "text-navy/70 hover:bg-navy/5"}`}><Home size={14} className="mr-1.5 inline" /> Units{!loading ? ` (${units.length})` : ""}</button>
        </div>

        {loading ? (
          <div className="mt-16 flex justify-center"><Loader2 className="animate-spin text-gold" size={30} /></div>
        ) : tab === "matches" ? (
          <MatchList matches={matchInbox} onStatus={setMatchStatus} onRemove={removeMatch} />
        ) : tab === "inquiries" ? (
          <InquiryList inquiries={inquiries} units={units} matches={matches} expanded={expanded}
            onOpenMatches={openMatches} onEdit={(d) => setForm({ type: "inquiry", data: d })} onDelete={(id) => remove("inquiry", id)}
            onStatus={(row, s) => changeStatus("inquiry", row, s)} onLink={linkUnit} />
        ) : (
          <UnitList units={units} onEdit={(d) => setForm({ type: "unit", data: d })} onDelete={(id) => remove("unit", id)}
            onStatus={(row, s) => changeStatus("unit", row, s)} />
        )}
      </main>

      {/* form drawer */}
      {form && (
        <div className="fixed inset-0 z-50 flex justify-end bg-navy/40 backdrop-blur-sm" onClick={() => !saving && setForm(null)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} data-testid="crm-form" className="h-full w-full max-w-lg overflow-y-auto bg-cream p-6 shadow-2xl sm:p-8">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-2xl font-semibold text-navy">{form.data.id ? "Edit" : "New"} {form.type}</h2>
              <button type="button" onClick={() => setForm(null)} className="rounded-full p-2 text-navy hover:bg-navy/5"><X size={22} /></button>
            </div>
            <div className="mt-6 grid grid-cols-2 gap-3">
              {form.type === "inquiry" && <>
                <div><label className="block text-xs font-medium text-navy/70">Buyer name</label><input className={field} data-testid="crm-buyer-name" value={form.data.buyer_name} onChange={set("buyer_name")} /></div>
                <div><label className="block text-xs font-medium text-navy/70">Buyer phone</label><input className={field} data-testid="crm-buyer-phone" value={form.data.buyer_phone} onChange={set("buyer_phone")} /></div>
              </>}
              {form.type === "unit" && <>
                <div><label className="block text-xs font-medium text-navy/70">Agent name</label><input className={field} data-testid="crm-agent-name" value={form.data.agent_name} onChange={set("agent_name")} /></div>
                <div><label className="block text-xs font-medium text-navy/70">Agent phone</label><input className={field} data-testid="crm-agent-phone" value={form.data.agent_phone} onChange={set("agent_phone")} /></div>
              </>}
              <div><label className="block text-xs font-medium text-navy/70">Property type</label>
                <select className={field} data-testid="crm-type" value={form.data.property_type} onChange={set("property_type")}>{TYPES.map((t) => <option key={t} value={t}>{t ? cap(t) : "—"}</option>)}</select></div>
              <div><label className="block text-xs font-medium text-navy/70">Bedrooms</label>
                <select className={field} data-testid="crm-beds" value={form.data.bedrooms} onChange={set("bedrooms")}>{BEDS.map((b) => <option key={b} value={b}>{b ? bedsLabel(b) : "—"}</option>)}</select></div>
              <div className="col-span-2"><label className="block text-xs font-medium text-navy/70">Community / area</label><input className={field} data-testid="crm-community" value={form.data.community} onChange={set("community")} placeholder="e.g. JVC, Business Bay" /></div>
              {form.type === "unit" ? (
                <div><label className="block text-xs font-medium text-navy/70">Price (AED)</label><input type="number" className={field} data-testid="crm-price" value={form.data.price} onChange={set("price")} /></div>
              ) : <>
                <div><label className="block text-xs font-medium text-navy/70">Budget min</label><input type="number" className={field} data-testid="crm-budget-min" value={form.data.budget_min} onChange={set("budget_min")} /></div>
                <div><label className="block text-xs font-medium text-navy/70">Budget max</label><input type="number" className={field} data-testid="crm-budget-max" value={form.data.budget_max} onChange={set("budget_max")} /></div>
              </>}
              {form.type === "unit" && <div><label className="block text-xs font-medium text-navy/70">Furnished</label>
                <select className={field} value={form.data.furnished === null ? "" : form.data.furnished} onChange={set("furnished")}><option value="">—</option><option value="yes">Yes</option><option value="no">No</option></select></div>}
              <div><label className="block text-xs font-medium text-navy/70">Status</label>
                <select className={field} data-testid="crm-status" value={form.data.status} onChange={set("status")}>{(form.type === "unit" ? UNIT_STATUS : INQ_STATUS).map((s) => <option key={s} value={s}>{cap(s)}</option>)}</select></div>
            </div>
            <label className="mt-4 block text-xs font-medium text-navy/70">Notes</label>
            <textarea rows={3} className={`${field} resize-none`} data-testid="crm-notes" value={form.data.notes} onChange={set("notes")} />
            <button type="submit" disabled={saving} data-testid="crm-save" className="btn-primary mt-6 w-full disabled:opacity-60">{saving ? <><Loader2 size={16} className="animate-spin" /> Saving...</> : "Save"}</button>
          </form>
        </div>
      )}

      {paste && <PasteModal paste={paste} setPaste={setPaste} onDone={load} guard={guard} />}
      <Toaster position="top-center" richColors />
    </div>
  );
}

/* ---------- Inquiry list ---------- */
function InquiryList({ inquiries, units, matches, expanded, onOpenMatches, onEdit, onDelete, onStatus, onLink }) {
  if (inquiries.length === 0) return <Empty label="inquiries" hint="Add a buyer requirement or paste from WhatsApp." />;
  const unitById = Object.fromEntries(units.map((u) => [u.id, u]));
  return (
    <div className="mt-6 space-y-3">
      {inquiries.map((q) => {
        const mm = matches[q.id];
        return (
          <div key={q.id} className="rounded-2xl bg-white p-5 shadow-soft" data-testid={`crm-inquiry-${q.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-display text-lg font-semibold text-navy">{q.buyer_name || "Unnamed buyer"}</h3>
                  {isStale(q.last_activity) && q.status !== "won" && q.status !== "lost" && <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700" data-testid="crm-stale"><AlarmClock size={11} /> Follow up</span>}
                </div>
                <p className="mt-1 text-sm text-navy/70">
                  Wants <b>{bedsLabel(q.bedrooms)}</b> {q.property_type || "property"}{q.community && <> in <b>{q.community}</b></>}
                  {(q.budget_min || q.budget_max) && <> · budget {q.budget_min ? `${fmt(q.budget_min)}–` : "up to "}{fmt(q.budget_max)} {q.currency}</>}
                </p>
                {q.notes && <p className="mt-1 text-xs text-muted">{q.notes}</p>}
              </div>
              <div className="flex items-center gap-2">
                {q.buyer_phone && <a href={waLink(q.buyer_phone)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full bg-wa/10 px-3 py-1.5 text-xs font-medium text-wa hover:bg-wa hover:text-white"><MessageCircle size={13} /> WhatsApp</a>}
        <StatusSelect value={q.status} options={INQ_STATUS} onChange={(s) => onStatus(q, s)} testid={`crm-status-inquiry-${q.id}`} />
              </div>
            </div>

            <div className="mt-4 flex items-center gap-2 border-t border-navy/8 pt-3">
              <button onClick={() => onOpenMatches(q)} data-testid={`crm-matches-btn-${q.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-gold/15 px-4 py-2 text-xs font-semibold text-gold hover:bg-gold hover:text-white">
                <Search size={13} /> {mm ? `${mm.length} matching unit${mm.length === 1 ? "" : "s"}` : "Find matches"} {expanded === q.id ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
              </button>
              {(q.linked_unit_ids?.length > 0) && <span className="text-xs text-navy/60">{q.linked_unit_ids.length} linked</span>}
              <button onClick={() => onEdit(q)} data-testid={`crm-edit-inquiry-${q.id}`} className="ml-auto inline-flex items-center gap-1 rounded-full border border-navy/15 px-3 py-1.5 text-xs font-medium text-navy hover:bg-navy hover:text-white"><Pencil size={12} /> Edit</button>
              <button onClick={() => onDelete(q.id)} data-testid={`crm-delete-inquiry-${q.id}`} className="inline-flex items-center rounded-full border border-red-200 px-2.5 py-1.5 text-red-500 hover:bg-red-500 hover:text-white"><Trash2 size={13} /></button>
            </div>

            {expanded === q.id && (
              <div className="mt-3 space-y-2" data-testid={`crm-matches-${q.id}`}>
                {!mm ? <div className="flex justify-center py-4"><Loader2 className="animate-spin text-gold" size={20} /></div>
                  : mm.length === 0 ? <p className="rounded-xl bg-sand/60 px-4 py-3 text-sm text-muted">No available units match yet. Add units or relax the criteria.</p>
                    : mm.map(({ unit, score, reasons }) => {
                      const linked = q.linked_unit_ids?.includes(unit.id);
                      return (
                        <div key={unit.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-navy/8 bg-sand/40 px-4 py-3">
                          <div>
                            <p className="text-sm font-medium text-navy">{bedsLabel(unit.bedrooms)} {unit.property_type} · {unit.community || "—"} · <span className="text-gold">{fmt(unit.price)} {unit.currency}</span></p>
                            <p className="mt-0.5 text-xs text-muted">Match {score}/4 · {reasons.join(", ")}{unit.agent_name && ` · ${unit.agent_name}`}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            {unit.agent_phone && <a href={waLink(unit.agent_phone)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full bg-wa/10 px-2.5 py-1.5 text-xs font-medium text-wa hover:bg-wa hover:text-white"><MessageCircle size={12} /></a>}
                            <button onClick={() => onLink(q, unit.id)} disabled={linked} data-testid={`crm-link-${q.id}-${unit.id}`}
                              className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold ${linked ? "bg-emerald-100 text-emerald-700" : "bg-navy text-white hover:bg-navy/80"}`}>
                              {linked ? <><Check size={12} /> Linked</> : <><Link2 size={12} /> Link</>}
                            </button>
                          </div>
                        </div>
                      );
                    })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ---------- Unit list ---------- */
function UnitList({ units, onEdit, onDelete, onStatus }) {
  if (units.length === 0) return <Empty label="units" hint="Add a unit or paste agent listings from WhatsApp." />;
  return (
    <div className="mt-6 grid gap-3 md:grid-cols-2">
      {units.map((u) => (
        <div key={u.id} className="rounded-2xl bg-white p-5 shadow-soft" data-testid={`crm-unit-${u.id}`}>
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-display text-lg font-semibold text-navy">{bedsLabel(u.bedrooms)} {u.property_type || "property"}</h3>
                {isStale(u.last_activity) && u.status === "available" && <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700"><AlarmClock size={11} /> Check</span>}
              </div>
              <p className="mt-1 text-sm text-navy/70">{u.community || "—"} · <span className="font-semibold text-gold">{fmt(u.price)} {u.currency}</span></p>
              <p className="mt-1 text-xs text-muted">{u.agent_name || "—"}{u.furnished === true ? " · Furnished" : u.furnished === false ? " · Unfurnished" : ""}</p>
              {u.notes && <p className="mt-1 text-xs text-muted">{u.notes}</p>}
            </div>
            <StatusSelect value={u.status} options={UNIT_STATUS} onChange={(s) => onStatus(u, s)} testid={`crm-status-unit-${u.id}`} />
          </div>
          <div className="mt-4 flex items-center gap-2 border-t border-navy/8 pt-3">
            {u.agent_phone && <a href={waLink(u.agent_phone)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full bg-wa/10 px-3 py-1.5 text-xs font-medium text-wa hover:bg-wa hover:text-white"><MessageCircle size={13} /> Agent</a>}
            <button onClick={() => onEdit(u)} data-testid={`crm-edit-unit-${u.id}`} className="ml-auto inline-flex items-center gap-1 rounded-full border border-navy/15 px-3 py-1.5 text-xs font-medium text-navy hover:bg-navy hover:text-white"><Pencil size={12} /> Edit</button>
            <button onClick={() => onDelete(u.id)} data-testid={`crm-delete-unit-${u.id}`} className="inline-flex items-center rounded-full border border-red-200 px-2.5 py-1.5 text-red-500 hover:bg-red-500 hover:text-white"><Trash2 size={13} /></button>
          </div>
        </div>
      ))}
    </div>
  );
}

function StatusSelect({ value, options, onChange, testid = "crm-status-select" }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className={`rounded-full px-3 py-1.5 text-xs font-semibold outline-none ${STATUS_STYLE[value] || "bg-navy/10 text-navy"}`}>
      {options.map((s) => <option key={s} value={s}>{cap(s)}</option>)}
    </select>
  );
}

/* ---------- Matches inbox ---------- */
function MatchList({ matches, onStatus, onRemove }) {
  const active = matches.filter((m) => m.status !== "dismissed");
  if (active.length === 0) return <Empty label="matches" hint="When a new unit fits an open buyer inquiry (or vice versa), the match lands here automatically." />;
  return (
    <div className="mt-6 space-y-3" data-testid="crm-match-list">
      {active.map((m) => {
        const u = m.unit, q = m.inquiry;
        return (
          <div key={m.id} className={`rounded-2xl bg-white p-5 shadow-soft ${m.status === "new" ? "ring-2 ring-gold/40" : ""}`} data-testid={`crm-match-${m.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                {m.status === "new" && <span className="rounded-full bg-gold px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">New</span>}
                {m.status === "actioned" && <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-700">Actioned</span>}
                <span className="text-xs font-semibold text-gold">Match {m.score}/4</span>
                <span className="text-xs text-muted">· {m.reasons.join(", ")}</span>
              </div>
            </div>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {/* buyer */}
              <div className="rounded-xl border border-navy/8 bg-gold/5 p-4">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-gold">Buyer wants</p>
                <p className="mt-1 text-sm font-medium text-navy">{q.buyer_name || "Unnamed buyer"}</p>
                <p className="text-sm text-navy/70">{bedsLabel(q.bedrooms)} {q.property_type || "property"}{q.community && ` · ${q.community}`}</p>
                <p className="text-xs text-muted">Budget {q.budget_min ? `${fmt(q.budget_min)}–` : "up to "}{fmt(q.budget_max)} {q.currency}</p>
                {q.buyer_phone && <a href={waLink(q.buyer_phone)} target="_blank" rel="noreferrer" data-testid={`crm-match-wa-buyer-${m.id}`} className="mt-2 inline-flex items-center gap-1 rounded-full bg-wa/10 px-3 py-1.5 text-xs font-medium text-wa hover:bg-wa hover:text-white"><MessageCircle size={12} /> Buyer</a>}
              </div>
              {/* unit */}
              <div className="rounded-xl border border-navy/8 bg-emerald-50/60 p-4">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">Available unit</p>
                <p className="mt-1 text-sm font-medium text-navy">{bedsLabel(u.bedrooms)} {u.property_type || "property"}{u.community && ` · ${u.community}`}</p>
                <p className="text-sm font-semibold text-gold">{fmt(u.price)} {u.currency}</p>
                <p className="text-xs text-muted">{u.agent_name || "—"}{u.source ? ` · ${u.source}` : ""}</p>
                {u.agent_phone && <a href={waLink(u.agent_phone)} target="_blank" rel="noreferrer" data-testid={`crm-match-wa-agent-${m.id}`} className="mt-2 inline-flex items-center gap-1 rounded-full bg-wa/10 px-3 py-1.5 text-xs font-medium text-wa hover:bg-wa hover:text-white"><MessageCircle size={12} /> Agent</a>}
              </div>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-navy/8 pt-3">
              {q.buyer_phone && (
                <a
                  href={waSend(q.buyer_phone, `Hi ${q.buyer_name || "there"}, I found a unit that matches what you're looking for: ${bedsLabel(u.bedrooms)} ${u.property_type || "property"}${u.community ? ` in ${u.community}` : ""} — AED ${fmt(u.price)}. Would you like more details or to arrange a viewing?`)}
                  target="_blank" rel="noreferrer" data-testid={`crm-match-send-${m.id}`}
                  className="inline-flex items-center gap-1.5 rounded-full bg-gold px-4 py-2 text-xs font-semibold text-white transition hover:-translate-y-0.5 hover:bg-gold-400"
                >
                  <MessageCircle size={13} /> Send unit to buyer
                </a>
              )}
              {u.agent_phone && (
                <a
                  href={waSend(u.agent_phone, `Hi ${u.agent_name || "there"}, is your ${bedsLabel(u.bedrooms)} ${u.property_type || "property"}${u.community ? ` in ${u.community}` : ""}${u.price ? ` (AED ${fmt(u.price)})` : ""} still available? I may have a buyer. Thanks!`)}
                  target="_blank" rel="noreferrer" data-testid={`crm-match-ask-agent-${m.id}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-navy/20 px-4 py-2 text-xs font-semibold text-navy transition hover:-translate-y-0.5 hover:bg-navy hover:text-white"
                >
                  <MessageCircle size={13} /> Ask agent if available
                </a>
              )}
              {m.status !== "actioned" && <button onClick={() => onStatus(m.id, "actioned")} data-testid={`crm-match-actioned-${m.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-4 py-2 text-xs font-medium text-white hover:bg-emerald-700"><Check size={13} /> Mark actioned</button>}
              <button onClick={() => onRemove(m.id)} data-testid={`crm-match-dismiss-${m.id}`} className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-navy/15 px-4 py-2 text-xs font-medium text-navy hover:bg-navy hover:text-white"><X size={13} /> Dismiss</button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Empty({ label, hint }) {
  return (
    <div className="mt-10 rounded-3xl border border-dashed border-navy/20 bg-white p-12 text-center" data-testid="crm-empty">
      <p className="font-display text-xl font-semibold text-navy">No {label} yet</p>
      <p className="mt-2 text-sm text-muted">{hint}</p>
    </div>
  );
}

/* ---------- Paste (AI) modal ---------- */
function PasteModal({ paste, setPaste, onDone, guard }) {
  const upd = (patch) => setPaste((p) => ({ ...p, ...patch }));

  const parse = async () => {
    if (!paste.text.trim()) { toast.error("Paste some messages first"); return; }
    upd({ busy: true });
    try {
      const { data } = await adminApi.crmParse(paste.text, paste.kind);
      const recs = data.records || [];
      if (recs.length === 0) toast.error("No records found in that text");
      const selected = {}; recs.forEach((_, i) => (selected[i] = true));
      upd({ records: recs, selected, busy: false });
    } catch (err) { guard(err); toast.error(err.response?.data?.detail || "AI parsing failed"); upd({ busy: false }); }
  };

  const addAll = async () => {
    const chosen = paste.records.filter((_, i) => paste.selected[i]);
    if (chosen.length === 0) { toast.error("Select at least one record"); return; }
    upd({ busy: true });
    try {
      for (const r of chosen) {
        if (r.record_type === "unit") await adminApi.createUnit(cleanUnit(r));
        else await adminApi.createInquiry(cleanInquiry(r));
      }
      toast.success(`Added ${chosen.length} record${chosen.length === 1 ? "" : "s"}`);
      setPaste(null); onDone();
    } catch (err) { guard(err); toast.error("Saving failed"); upd({ busy: false }); }
  };

  const cleanUnit = (r) => ({ property_type: r.property_type || "", deal_type: r.deal_type || "", bedrooms: String(r.bedrooms || ""), community: r.community || "", price: r.price ?? null, currency: "AED", furnished: r.furnished ?? null, agent_name: r.agent_name || "", agent_phone: String(r.agent_phone || ""), source: "WhatsApp", notes: r.notes || "", status: "available" });
  const cleanInquiry = (r) => ({ buyer_name: r.buyer_name || "", buyer_phone: String(r.buyer_phone || ""), property_type: r.property_type || "", deal_type: r.deal_type || "", bedrooms: String(r.bedrooms || ""), community: r.community || "", budget_min: r.budget_min ?? null, budget_max: r.budget_max ?? null, currency: "AED", notes: r.notes || "", status: "new" });

  return (
    <div className="fixed inset-0 z-[55] flex items-center justify-center bg-navy/50 p-4 backdrop-blur-sm" onClick={() => !paste.busy && setPaste(null)}>
      <div onClick={(e) => e.stopPropagation()} className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-3xl bg-cream p-6 shadow-2xl sm:p-8" data-testid="crm-paste-modal">
        <div className="flex items-center justify-between">
          <h2 className="flex items-center gap-2 font-display text-2xl font-semibold text-navy"><Sparkles size={20} className="text-gold" /> Paste from WhatsApp</h2>
          <button onClick={() => setPaste(null)} className="rounded-full p-2 text-navy hover:bg-navy/5"><X size={22} /></button>
        </div>
        <p className="mt-2 text-sm text-muted">Copy one or more messages from your group and paste below. AI will split and fill in the fields — you review and add.</p>

        {!paste.records ? <>
          <textarea rows={7} className={`${field} mt-4 resize-none`} data-testid="crm-paste-text" value={paste.text} onChange={(e) => upd({ text: e.target.value })} placeholder="Paste WhatsApp messages here..." />
          <div className="mt-3 flex items-center gap-2">
            <span className="text-xs text-navy/60">These are:</span>
            {[["auto", "Auto-detect"], ["unit", "Units for sale"], ["inquiry", "Buyer inquiries"]].map(([k, l]) => (
              <button key={k} onClick={() => upd({ kind: k })} className={`rounded-full px-3 py-1.5 text-xs font-medium ${paste.kind === k ? "bg-navy text-white" : "bg-white text-navy/70 hover:bg-navy/5"}`}>{l}</button>
            ))}
          </div>
          <button onClick={parse} disabled={paste.busy} data-testid="crm-parse-run" className="btn-primary mt-5 w-full disabled:opacity-60">{paste.busy ? <><Loader2 size={16} className="animate-spin" /> Reading...</> : <><Sparkles size={16} /> Extract records</>}</button>
        </> : <>
          <div className="mt-4 space-y-2">
            {paste.records.map((r, i) => (
              <label key={i} className="flex cursor-pointer items-start gap-3 rounded-xl border border-navy/10 bg-white p-4" data-testid={`crm-parsed-${i}`}>
                <input type="checkbox" checked={!!paste.selected[i]} onChange={(e) => upd({ selected: { ...paste.selected, [i]: e.target.checked } })} className="mt-1 h-4 w-4 accent-gold" />
                <div>
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${r.record_type === "unit" ? "bg-emerald-100 text-emerald-700" : "bg-gold/15 text-gold"}`}>{r.record_type === "unit" ? "Unit" : "Inquiry"}</span>
                  <p className="mt-1.5 text-sm font-medium text-navy">{bedsLabel(String(r.bedrooms || ""))} {r.property_type || "property"}{r.community && ` · ${r.community}`}</p>
                  <p className="text-xs text-muted">
                    {r.record_type === "unit" ? `Price ${fmt(r.price)} AED` : `Budget ${r.budget_min ? fmt(r.budget_min) + "–" : "up to "}${fmt(r.budget_max)} AED`}
                    {(r.agent_name || r.buyer_name) && ` · ${r.agent_name || r.buyer_name}`}{(r.agent_phone || r.buyer_phone) && ` · ${r.agent_phone || r.buyer_phone}`}
                  </p>
                </div>
              </label>
            ))}
          </div>
          <div className="mt-5 flex gap-2">
            <button onClick={() => upd({ records: null })} className="btn-ghost flex-1">Back</button>
            <button onClick={addAll} disabled={paste.busy} data-testid="crm-add-all" className="btn-primary flex-1 disabled:opacity-60">{paste.busy ? <><Loader2 size={16} className="animate-spin" /> Adding...</> : "Add selected"}</button>
          </div>
        </>}
      </div>
    </div>
  );
}
